import React, { useEffect, useState } from 'react';
import { Typography, Box, Button, Grid, Dialog, DialogContent, useMediaQuery, Select, MenuItem } from '@mui/material';
import CloseModalIcon from '../../../icons/RoundCloseIcon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { PaymentRequestInfo, getBuildGenericPaymentRequestDto, selectPaymentRequestsState } from '../sagas/paymentrequestslice';
import { useStyles } from '../../../style/Pages/BasicCompanyInfo';
import { InputField } from '../../../components/InputController';
import { useFormik } from 'formik';
import { getAllPropertyGroupsConfigs, selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import * as Yup from 'yup';
import RequestDetailsModal from './RequestDetailsModal';
import { EmailRegex } from '../../../utils/regex';
import { AssignedConfigsDto, OwnerV2Dto } from '@inosio/payments-sdk';
import { getAvailableBankAccount } from '../../billingConfiguration/sagas/billingSlice';

interface OwnerCreatePaymentRequestModalProps {
  open: boolean;
  onClose: () => void;
  ownerDto: OwnerV2Dto;
}

const schemaValidationOwnerPayRequestSetup = Yup.object({
  first_name: Yup.string().required('Required First Name'),
  last_name: Yup.string().required('Required Last Name'),
  payer_email: Yup.string().matches(EmailRegex, 'Please type a valid Email.').required('Required'),
});

type schemaValidationOwnerPayRequestSetupType = Yup.InferType<typeof schemaValidationOwnerPayRequestSetup>;

const OwnerCreatePaymentRequestModal = (props: OwnerCreatePaymentRequestModalProps) => {
  //constants
  const dispatch = useDispatch();
  const classes = useStyles();
  const isOnMobileScreen = useMediaQuery('(max-width:600px)');
  const style = {
    height: isOnMobileScreen ? '' : '555px',
  };

  const payerInitialValues: schemaValidationOwnerPayRequestSetupType = {
    first_name: props.ownerDto.UserProfiles?.[0]?.FirstName || props.ownerDto.FirstName,
    last_name: props.ownerDto.UserProfiles?.[0]?.LastName || props.ownerDto.LastName,
    payer_email: props.ownerDto.UserProfiles?.[0]?.UserEmail || props.ownerDto.IntegrationEmail,
  };

  const formikValidator = useFormik<schemaValidationOwnerPayRequestSetupType>({
    initialValues: payerInitialValues,
    validationSchema: schemaValidationOwnerPayRequestSetup,
    onSubmit: async (values) => {
      //creating payment request all in one go at the end.
      return;
    },
  });

  //local states
  const [selectedPropertyUuid, setSelectedPropertyUuid] = useState('');
  const [openRequestDetails, setOpenRequestDetails] = useState(false);
  const [pgConfigsForSelectedProp, setPgConfigsForSelectedProp] = useState<AssignedConfigsDto | null>();
  const [localErrorMessage, setLocalErrorMessage] = useState('');

  //selectors
  const { paymentRequestsError } = useSelector(selectPaymentRequestsState);
  const { allPropertyGroupsAssignedConfigs, shouldRefreshAllPropertyGroupConfigs } = useSelector(selectPropertyGroupDetailsState);

  //useEffects

  //makes sure we get the updated configs and display them on the ui
  useEffect(() => {
    if (shouldRefreshAllPropertyGroupConfigs) dispatch(getAllPropertyGroupsConfigs());
  }, [shouldRefreshAllPropertyGroupConfigs]);

  useEffect(() => {
    if (props.ownerDto.Properties?.length === 1) {
      //if there is only one, then just auto select it
      setSelectedPropertyUuid(props.ownerDto.Properties[0].Uuid);
    }
  }, [props.ownerDto.Properties]);

  useEffect(() => {
    if (selectedPropertyUuid) {
      const pgUuidOfSelectedProp = props.ownerDto.Properties.find((p) => p.Uuid === selectedPropertyUuid)?.PropertyGroupUuid;
      const selectedPg = allPropertyGroupsAssignedConfigs.find((pg) => pg.Uuid === pgUuidOfSelectedProp);
      setPgConfigsForSelectedProp(selectedPg);
    }
  }, [selectedPropertyUuid]);

  //on load fetch all bank accounts user has access to
  useEffect(() => {
    dispatch({ type: getAvailableBankAccount.type, payload: { fundableOnly: true } });
  }, []);

  //The issue is that Formik only sets its initial values when the component first mounts. So, even if the ownerDto prop (like FirstName, LastName, or Email) changes later, Formik doesn't update the form values automatically.
  //Hence if props.ownerDto changes, we reset the form values
  useEffect(() => {
    if (props.ownerDto) {
      formikValidator.setValues({
        first_name: props.ownerDto.UserProfiles?.[0]?.FirstName || props.ownerDto.FirstName || '',
        last_name: props.ownerDto.UserProfiles?.[0]?.LastName || props.ownerDto.LastName || '',
        payer_email: props.ownerDto.UserProfiles?.[0]?.UserEmail || props.ownerDto.IntegrationEmail || '',
      });
    }
  }, [props.ownerDto]);

  //error handling
  useEffect(() => {
    if (!!paymentRequestsError?.message?.length) {
      setLocalErrorMessage(paymentRequestsError?.message);
    }
  }, [paymentRequestsError]);

  //handlers
  const handleContinue = () => {
    //only let them continue if the there is a configured deposit bank account for the property they selected
    if (pgConfigsForSelectedProp?.OwnerPaymentDestinationBankAccountUuid) {
      setOpenRequestDetails(true);
      //this is just for passing states between modals, actually api request is in the review modal
      dispatch({
        type: getBuildGenericPaymentRequestDto.type,
        payload: {
          PayeeUuid: pgConfigsForSelectedProp?.Uuid, //property group
          DepositBankAccountUuid: pgConfigsForSelectedProp?.OwnerPaymentDestinationBankAccountUuid, // from the configs for that property group
          PropertyUuid: selectedPropertyUuid,
          IsPayerGeneric: false,
          PayerInfo: {
            Uuid: props.ownerDto.Uuid,
            Email: formikValidator.values.payer_email,
            FirstName: formikValidator.values.first_name,
            LastName: formikValidator.values.last_name,
          },
        } as PaymentRequestInfo,
      });
    } else {
      setLocalErrorMessage('The selected property does not have a configured deposit bank account.');
    }
  };

  const handleClose = () => {
    props.onClose();
    setLocalErrorMessage('');
    setSelectedPropertyUuid('');
  };

  return (
    <React.Fragment>
      <Dialog
        open={props.open}
        onClose={handleClose}
        fullScreen={isOnMobileScreen}
        BackdropProps={{
          invisible: false,
        }}
        fullWidth={true}
        disableEnforceFocus
        style={{
          background: 'none',
        }}
        PaperProps={{
          style: {
            pointerEvents: 'auto',
            width: '520px',
            borderRadius: '24px',
          },
        }}
      >
        <DialogContent>
          <Box sx={style}>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={2}>
                {' '}
              </Grid>
              <Grid item xs={8}>
                <Typography
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: '18px',
                    lineHeight: '22px',
                    fontFamily: 'Work Sans',
                  }}
                >
                  Request Details
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <img src={CloseModalIcon} alt="close" style={{ cursor: 'pointer', marginLeft: isOnMobileScreen ? '0px' : '40px' }} onClick={handleClose} />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                {localErrorMessage ? (
                  <div className={classes.globalError}>
                    <Typography
                      style={{
                        fontFamily: "'Open Sans', sans-serif",
                        fontStyle: 'normal',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '18px',
                        color: '#DB3939',
                      }}
                    >
                      {localErrorMessage}
                    </Typography>
                  </div>
                ) : (
                  ''
                )}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              Enter Payer Details:
            </Grid>
            <Grid item xs={12}>
              &nbsp;
            </Grid>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={6}>
                <InputField
                  name="first_name"
                  label="First Name"
                  isError={false}
                  errors={formikValidator.errors?.first_name}
                  touched={formikValidator.touched?.first_name}
                  onBlur={formikValidator.handleBlur}
                  onChange={(e) => formikValidator.setFieldValue('first_name', e.target.value)}
                  value={formikValidator.values.first_name ?? ''}
                />
              </Grid>
              <Grid item xs={6}>
                <InputField
                  name="last_name"
                  label="Last Name"
                  isError={false}
                  errors={formikValidator.errors?.last_name}
                  touched={formikValidator.touched?.last_name}
                  onBlur={formikValidator.handleBlur}
                  onChange={(e) => formikValidator.setFieldValue('last_name', e.target.value)}
                  value={formikValidator.values.last_name ?? ''}
                />
              </Grid>
              <Grid item xs={12}>
                <InputField
                  name="email"
                  label="Email"
                  isError={false}
                  errors={formikValidator.errors?.payer_email}
                  touched={formikValidator.touched?.payer_email}
                  onBlur={formikValidator.handleBlur}
                  onChange={(e) => formikValidator.setFieldValue('payer_email', e.target.value)}
                  value={formikValidator.values.payer_email ?? ''}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              Select Property:
            </Grid>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={12}>
                <Select
                  value={selectedPropertyUuid ?? ''}
                  displayEmpty
                  style={{ borderRadius: '24px', height: '42px' }}
                  onChange={(e) => {
                    setLocalErrorMessage('');
                    setSelectedPropertyUuid(e.target.value);
                  }}
                >
                  <MenuItem disabled value={''}>
                    <Typography>Select Property</Typography>
                  </MenuItem>
                  {props.ownerDto.Properties?.map((p) => (
                    <MenuItem value={p.Uuid} key={p.Uuid} selected={p.Uuid === selectedPropertyUuid}>
                      <Typography>{p.Name}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>

            <Grid item sx={{ position: 'absolute', bottom: 15, left: '190px' }} ml={20}>
              <Button
                variant="contained"
                style={{
                  width: '139px',
                  height: '42px',
                  borderRadius: '24px',
                  textTransform: 'capitalize',
                  color: 'white',
                  backgroundColor: !formikValidator.isValid || !selectedPropertyUuid ? 'rgba(211, 211, 211, 1)' : '#39A2DB',
                  borderColor: '#39A2DB',
                }}
                disabled={!formikValidator.isValid || !selectedPropertyUuid}
                onClick={handleContinue}
                id="continuePayerBtn"
              >
                continue
              </Button>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
      <RequestDetailsModal
        open={openRequestDetails}
        propertyGroupUuid={pgConfigsForSelectedProp?.Uuid || ''}
        isInvisible={true}
        onClose={() => {
          props.onClose();
          setOpenRequestDetails(false);
        }}
        onGoBack={() => {
          setOpenRequestDetails(false);
        }}
      />
    </React.Fragment>
  );
};

export default OwnerCreatePaymentRequestModal;
